import * as React from 'react'
import PropTypes from 'prop-types'
import { CustomPropTypes } from '../../support/CustomPropTypes'
import {
  BaseButton,
  baseButtonKinds,
  BaseButtonProps,
  baseButtonSizes,
  baseButtonTypes,
} from '../../internal/components/BaseButton'
import { consoleWarn } from '../../logging'
import { Tooltip, TooltipProps } from '../Tooltip'
import { TooltipManager } from '../TooltipManager'
import { useThemeStyles } from '../../theming'
import styles from './styles'
import { AlertBadgeProps } from '../AlertBadge/AlertBadge'

export interface IconButtonProps
  extends Omit<BaseButtonProps, 'styleOverrides'> {
  /**
   * The text to present to assistive devices in order to identify the IconButton.
   */
  accessibilityText: string
  /**
   * Displays an alert badge at the top-end of the IconButton. Accepts an <AlertBadge /> component.
   */
  badge?: React.ReactElement<AlertBadgeProps>
  /**
   * Disables the Tooltip from showing. Note: Tooltips will not be shown when the IconButton's `disabled` prop is set to true.
   */
  disableTooltip?: boolean
  /**
   * The icon from `@ds/icons` to show inside the IconButton.
   */
  icon: React.ReactElement<React.SVGProps<SVGSVGElement>>
  /**
   * This allows you to add a custom `Tooltip` component to set custom text, alignment and location.
   */
  tooltip?: React.ReactElement<TooltipProps>
}

/**
 *  An IconButton is used when an icon is well-suited to conveying an action on its own.
 */
export function IconButton({
  accessibilityText,
  badge,
  'data-qa': dataQA,
  disableTooltip = false,
  disabled = false,
  icon,
  kind = 'tertiary',
  loading,
  size = 'medium',
  tooltip,
  ...restProps
}: IconButtonProps) {
  const sx = useThemeStyles(styles, { kind, size })

  if (loading && kind === 'danger') {
    consoleWarn(`
        When using an IconButton component, the "danger" value of the "kind" prop and the "loading" prop cannot
        be used at the same time.
      `)
  }

  const isLoading = loading && kind !== 'danger'
  const childNode = isLoading ? null : icon
  const hasNoTooltip = disabled || disableTooltip

  const badgeNode = badge ? <div css={sx.button.badge}>{badge}</div> : undefined

  const baseButtonNode = (
    <BaseButton
      {...restProps}
      accessibilityText={accessibilityText}
      data-qa={dataQA}
      disabled={disabled}
      kind={kind}
      loading={isLoading}
      size={size}
      styleOverrides={sx.button.default}
    >
      {childNode}
      {badgeNode}
    </BaseButton>
  )

  return hasNoTooltip ? (
    baseButtonNode
  ) : (
    <TooltipManager
      tooltip={
        tooltip || (
          <Tooltip
            data-qa={dataQA && `${dataQA}-tooltip`}
            text={accessibilityText}
          />
        )
      }
    >
      {baseButtonNode}
    </TooltipManager>
  )
}

IconButton.propTypes = {
  accessibilityText: PropTypes.string.isRequired,
  activated: PropTypes.bool,
  'aria-.*': PropTypes.string,
  'data-qa': PropTypes.string,
  disabled: PropTypes.bool,
  disableTooltip: PropTypes.bool,
  forwardedRef: CustomPropTypes.ReactRef,
  icon: PropTypes.element.isRequired,
  kind: PropTypes.oneOf(baseButtonKinds),
  loading: PropTypes.bool,
  'on[A-Z].*': PropTypes.func,
  size: PropTypes.oneOf(baseButtonSizes),
  title: PropTypes.string,
  tooltip: PropTypes.element,
  type: PropTypes.oneOf(baseButtonTypes),
}
